<template>
  <FlowLayout v-if="shouldUseFlowLayout">
    <router-view />
  </FlowLayout>
  <div v-else class="min-h-screen bg-background">
    <Header v-if="showHeader" />
    <router-view />
  </div>
  <ProfileDrawer
    :model-value="isSettingsDrawerOpen"
    side="right"
    variant="close"
    @update:model-value="handleSettingsDrawerUpdate"
    @logout="handleLogout"
  />
  <!-- Login: drawer on mobile, modal on web -->
  <UiDrawer
    v-if="windowWidth < 768"
    :model-value="isLoginDrawerOpen"
    side="bottom"
    :height="drawerHeight"
    :close-on-backdrop="true"
    :close-on-esc="true"
    animation="slide-fade"
    border-radius="20px"
    @update:model-value="handleLoginDrawerUpdate"
  >
    <template #header>
      <div class="flex justify-end items-center py-4 px-5">
        <button
          class="flex items-center justify-center w-8 h-8 bg-transparent border-none cursor-pointer text-black transition-opacity duration-200 p-0 hover:opacity-70"
          @click="handleLoginDrawerUpdate(false)"
          aria-label="Close"
        >
          <PhX :size="24" weight="regular" />
        </button>
      </div>
    </template>
    <div class="h-full flex flex-col overflow-hidden">
      <LoginForm />
    </div>
  </UiDrawer>
  <Modal
    v-else
    :model-value="isLoginDrawerOpen"
    :show-actions="false"
    max-width="sm"
    @update:model-value="handleLoginDrawerUpdate"
  >
    <LoginForm />
  </Modal>

  <!-- Register: drawer on mobile, modal on web -->
  <UiDrawer
    v-if="windowWidth < 768"
    :model-value="isRegisterDrawerOpen"
    side="bottom"
    :height="drawerHeight"
    :close-on-backdrop="true"
    :close-on-esc="true"
    animation="slide-fade"
    border-radius="20px"
    @update:model-value="handleRegisterDrawerUpdate"
  >
    <template #header>
      <div class="flex justify-end items-center py-4 px-5">
        <button
          class="flex items-center justify-center w-8 h-8 bg-transparent border-none cursor-pointer text-black transition-opacity duration-200 p-0 hover:opacity-70"
          @click="handleRegisterDrawerUpdate(false)"
          aria-label="Close"
        >
          <PhX :size="24" weight="regular" />
        </button>
      </div>
    </template>
    <div class="h-full flex flex-col overflow-hidden">
      <RegisterForm />
    </div>
  </UiDrawer>
  <Modal
    v-else
    :model-value="isRegisterDrawerOpen"
    :show-actions="false"
    max-width="sm"
    @update:model-value="handleRegisterDrawerUpdate"
  >
    <RegisterForm />
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useOrderStore } from '@/stores/order'
import { useAppFlow } from '@/composables/core/useAppFlow'
import Header from '@/components/organisms/Header.vue'
import FlowLayout from '@/components/layouts/FlowLayout.vue'
import ProfileDrawer from '@/components/features/profile/ProfileDrawer.vue'
import UiDrawer from '@/components/common/UiDrawer.vue'
import Modal from '@/components/common/Modal.vue'
import LoginForm from '@/components/features/auth/LoginForm.vue'
import RegisterForm from '@/components/features/auth/RegisterForm.vue'
import { PhX } from '@phosphor-icons/vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const orderStore = useOrderStore()
const { currentFlow, explicitFlow } = useAppFlow()

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)

const drawerHeight = computed(() => {
  return windowWidth.value < 768 ? '80vh' : '90vh'
})

const handleResize = (): void => {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
  if (currentFlow.value === 'smart-dine-in') {
    orderStore.loadCartFromLocalStorage()
  } else {
    orderStore.clearCartInMemoryOnly()
  }
})

watch(
  () => currentFlow.value,
  flow => {
    if (flow === 'smart-dine-in') {
      orderStore.loadCartFromLocalStorage()
    } else {
      orderStore.clearCartInMemoryOnly()
    }
  }
)

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

const isSettingsDrawerOpen = computed(() => route.query.drawer === 'settings')
const isLoginDrawerOpen = computed(() => route.query.drawer === 'login')
const isRegisterDrawerOpen = computed(() => route.query.drawer === 'register')

const handleSettingsDrawerUpdate = (value: boolean): void => {
  if (value) {
    router.push({
      path: route.path,
      query: { ...route.query, drawer: 'settings' },
    })
  } else {
    const newQuery = { ...route.query }
    delete newQuery.drawer
    router.replace({
      path: route.path,
      query: newQuery,
    })
  }
}

const handleLoginDrawerUpdate = (value: boolean): void => {
  if (value) {
    router.push({
      path: route.path,
      query: { ...route.query, drawer: 'login' },
    })
  } else {
    const newQuery = { ...route.query }
    delete newQuery.drawer
    router.replace({
      path: route.path,
      query: newQuery,
    })
  }
}

const handleRegisterDrawerUpdate = (value: boolean): void => {
  if (value) {
    router.push({
      path: route.path,
      query: { ...route.query, drawer: 'register' },
    })
  } else {
    const newQuery = { ...route.query }
    delete newQuery.drawer
    router.replace({
      path: route.path,
      query: newQuery,
    })
  }
}

const handleLogout = async (): Promise<void> => {
  await authStore.logout()
}

// Routes that should use FlowLayout (all flow-aware routes)
// FlowLayout handles bottom nav visibility automatically via route meta
const flowRoutes = ['Home', 'Restaurants', 'Reservations', 'Profile', 'RestaurantMenu', 'Cart']

const shouldUseFlowLayout = computed(() => {
  if (
    (route.name === 'MyOrders' || route.name === 'MyReservations') &&
    explicitFlow.value !== null
  ) {
    return true
  }

  return flowRoutes.includes(route.name as string)
})

const showHeader = computed(() => {
  if (route.name === 'ReservationRestaurant') {
    return currentFlow.value === 'smart-dine-in'
  }

  return route.meta.hideHeader !== true
})
</script>
